import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { LocaleService, MainState, ModalService, TranslationPipe, TranslationService } from '@cloud-and-service/portal-lib';
import { Store } from '@ngxs/store';
import { LanguageSelectorComponent } from 'src/app/components/language-selector/language-selector.component';
import { PasswordResultReason } from 'src/app/models/password-reset';
import { SafeHtmlPipe } from 'src/app/pipes/safe-html.pipe';
import { PasswordService } from 'src/app/services/password.service';

export type SetPasswordState = 'loading' | 'ready' | 'invalid' | 'error' | 'done';

const REASON_TRANSLATION_KEYS: Record<string, string> = {
  expired: 'ACTIVATION_LINK_EXPIRED',
  used: 'ACTIVATION_LINK_USED',
  invalid: 'ACTIVATION_LINK_INVALID',
};

/**
 * Landing page for the activation link e-mailed by Business Central (first time and "forgot password").
 * The token is opaque to the portal; Business Central validates it and supplies the password regex.
 */
@Component({
  selector: 'app-set-password',
  templateUrl: './set-password.component.html',
  styleUrls: ['../login/login.component.scss'],
  imports: [
    FormsModule,
    RouterLink,
    SafeHtmlPipe,
    TranslationPipe,
    LanguageSelectorComponent,
  ],
})
export class SetPasswordComponent implements OnInit {
  private readonly passwordService = inject(PasswordService);
  private readonly localeService = inject(LocaleService);
  private readonly modalService = inject(ModalService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly translation = inject(TranslationService);

  /** Bound from the `:forsyning` route param and the `token` query param (withComponentInputBinding). */
  readonly routePrefix = input<string | undefined>(undefined, { alias: 'forsyning' });
  readonly token = input<string | undefined>();

  readonly logo = this.store.selectSignal(MainState.logo);
  readonly title = this.store.selectSignal(MainState.title);

  readonly state = signal<SetPasswordState>('loading');
  readonly reason = signal<PasswordResultReason | undefined>(undefined);
  readonly requirementsText = signal<string | null>(null);
  readonly passwordPattern = signal<RegExp | null>(null);
  readonly password = signal('');
  readonly confirmPassword = signal('');
  readonly policyRejected = signal(false);
  readonly saving = signal(false);

  readonly meetsPolicy = computed(() => {
    const password = this.password();
    if (!password) {
      return false;
    }

    // Without a usable regex the API and Business Central still enforce the policy on save.
    const pattern = this.passwordPattern();
    return pattern ? pattern.test(password) : true;
  });

  readonly passwordsMatch = computed(() => this.password() === this.confirmPassword());
  readonly canSubmit = computed(() => this.meetsPolicy() && this.passwordsMatch() && !this.saving());
  readonly reasonKey = computed(() => REASON_TRANSLATION_KEYS[this.reason() ?? 'invalid'] ?? REASON_TRANSLATION_KEYS['invalid']);
  readonly loginLink = computed(() => this.routePrefix() ? ['/', this.routePrefix(), 'login'] : ['/login']);
  readonly forgotPasswordLink = computed(() => this.routePrefix() ? ['/', this.routePrefix(), 'forgot-password'] : ['/forgot-password']);

  async ngOnInit(): Promise<void> {
    this.modalService.closeAllModals();

    const routePrefix = this.routePrefix();
    const token = this.token();
    if (!routePrefix || !token) {
      await this.showInvalid('invalid');
      return;
    }

    try {
      const result = await this.passwordService.validateActivationToken(routePrefix, token, this.localeService.currentLanguageSignal());
      if (!result.valid) {
        await this.showInvalid(result.reason, result.messageText);
        return;
      }

      this.requirementsText.set(result.passwordRequirementsText || null);
      this.passwordPattern.set(SetPasswordComponent.toRegExp(result.passwordRegex));
      this.state.set('ready');
    } catch (error) {
      this.state.set('error');
      this.modalService.error(String(error));
    }
  }

  async save(): Promise<void> {
    const routePrefix = this.routePrefix();
    const token = this.token();
    if (!this.canSubmit() || !routePrefix || !token) {
      return;
    }

    this.saving.set(true);
    this.policyRejected.set(false);
    try {
      const result = await this.passwordService.setPassword(routePrefix, token, this.password());
      if (result.success) {
        this.state.set('done');
        await this.modalService.info(result.messageText || this.translation.getTranslation('SET_PASSWORD_DONE'));
        await this.goToLogin();
      } else if (result.reason === 'policy') {
        this.policyRejected.set(true);
        await this.modalService.info(result.messageText || this.translation.getTranslation('SET_PASSWORD_POLICY'));
      } else {
        await this.showInvalid(result.reason, result.messageText);
      }
    } catch (error) {
      this.modalService.error(String(error));
    } finally {
      this.saving.set(false);
    }
  }

  /** Shows the text from Business Central (or a translated fallback) in a popup, then returns to the login page. */
  private async showInvalid(reason: PasswordResultReason | undefined, messageText?: string): Promise<void> {
    this.reason.set(reason);
    this.state.set('invalid');
    await this.modalService.info(messageText || this.translation.getTranslation(this.reasonKey()));
    await this.goToLogin();
  }

  private async goToLogin(): Promise<void> {
    await this.router.navigate(this.routePrefix() ? ['/', this.routePrefix(), 'login'] : ['/']);
  }

  /** The regex comes from Business Central (.NET); if the browser cannot parse it, skip the live check. */
  static toRegExp(pattern: string | undefined): RegExp | null {
    if (!pattern) {
      return null;
    }

    try {
      return new RegExp(pattern);
    } catch {
      return null;
    }
  }
}
